import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {
  ExpenditureState,
  FetchExpendituresResponse,
  FetchExpenditureByIdResponse,
  CreateExpenditureResponse,
  UpdateExpenditureResponse,
  DeleteExpenditureResponse,
} from './types'
import {
  fetchExpenditureById,
  fetchExpenditures,
  createExpenditure,
  updateExpenditure,
  deleteExpenditure,
} from './service'

const initialState: ExpenditureState = {
  expenditureForEdit: null,
  expenditures: [],
  total: 0,
  totalPages: 0,
  progress: 0,
  listLoading: false,
  actionLoading: false,
  error: null,
}

const expenditureSlice = createSlice({
  name: 'expenditure',
  initialState,
  reducers: {
    handleRequestProgress: (state: ExpenditureState, action: any) => {
      const {progress} = action.payload
      state.progress = progress
    },
    resetExpenditures: (state: ExpenditureState) => {
      state.expenditures = []
      state.total = 0
      state.totalPages = 0
    },
  },
  extraReducers: (builder) => {
    // Fetch Expenditures
    builder.addCase(fetchExpenditures.pending, (state: ExpenditureState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(
      fetchExpenditures.rejected,
      (state: ExpenditureState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.listLoading = false
      }
    )
    builder.addCase(
      fetchExpenditures.fulfilled,
      (state: ExpenditureState, action: PayloadAction<FetchExpendituresResponse>) => {
        const {expenses, total, totalPages} = action.payload
        state.expenditures = expenses || []
        state.total = total
        state.totalPages = totalPages
        state.listLoading = false
      }
    )

    // Fetch Expenditure By ID
    builder.addCase(fetchExpenditureById.pending, (state: ExpenditureState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      fetchExpenditureById.rejected,
      (state: ExpenditureState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      fetchExpenditureById.fulfilled,
      (state: ExpenditureState, action: PayloadAction<FetchExpenditureByIdResponse>) => {
        const {expense} = action.payload
        state.expenditureForEdit = expense
        state.actionLoading = false
      }
    )

    // Create Expenditure
    builder.addCase(createExpenditure.pending, (state: ExpenditureState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      createExpenditure.rejected,
      (state: ExpenditureState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      createExpenditure.fulfilled,
      (state: ExpenditureState, action: PayloadAction<CreateExpenditureResponse>) => {
        const {expense} = action.payload
        state.expenditures.unshift(expense) // Add to beginning of list
        state.total += 1
        state.actionLoading = false
      }
    )

    // Update Expenditure
    builder.addCase(updateExpenditure.pending, (state: ExpenditureState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      updateExpenditure.rejected,
      (state: ExpenditureState, action: any) => {
        console.error('Update expenditure rejected:', action)
        const error = action.payload?.error || action.error?.message || 'Failed to update'
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      updateExpenditure.fulfilled,
      (state: ExpenditureState, action: PayloadAction<UpdateExpenditureResponse>) => {
        console.log('Update expenditure fulfilled:', action.payload)
        const {expense} = action.payload
        if (expense && expense._id) {
          const index = state.expenditures.findIndex((e) => e._id === expense._id)
          if (index !== -1) {
            state.expenditures[index] = expense
          }
          state.expenditureForEdit = expense
        }
        state.actionLoading = false
      }
    )

    // Delete Expenditure
    builder.addCase(deleteExpenditure.pending, (state: ExpenditureState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      deleteExpenditure.rejected,
      (state: ExpenditureState, action: any) => {
        console.error('Delete expenditure rejected:', action)
        const error = action.payload?.error || action.error?.message || 'Failed to delete'
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      deleteExpenditure.fulfilled,
      (state: ExpenditureState, action: PayloadAction<DeleteExpenditureResponse>) => {
        console.log('Delete expenditure fulfilled:', action.payload)
        const {expense} = action.payload
        if (expense && expense._id) {
          state.expenditures = state.expenditures.filter((e) => e._id !== expense._id)
          state.total = Math.max(0, state.total - 1)
        }
        state.actionLoading = false
      }
    )
  },
})

export const actions = expenditureSlice.actions
export default expenditureSlice.reducer
